// Mesh network + lower/upper transport layers — pure bytes, no radio.
//
// Ported from the cob-mesh-reactjs sampler (src/mesh/network.ts). One subnet,
// IV index fixed by the caller (0 today), no IV update, no key refresh.
//
// `keys.nextSeq()` must hand out a 24-bit sequence number that THIS phone has
// never used with THIS source address — a node silently drops anything it has
// seen from (src, seq) before. That is meshClientStore's job; see the note there.

import {
  aesEcb, ccmDecrypt, ccmEncrypt, k2, k4,
} from './meshCrypto';
import {
  concat, readU16be, u16be, u24be, u32be,
} from './bytes';

/* eslint-disable no-bitwise */

export const DEFAULT_TTL = 5;

export const encodeOpcode = (op) => {
  if (op <= 0x7e) return Uint8Array.of(op);
  return op <= 0xffff ? u16be(op) : u24be(op);
};

export const parseOpcode = (b) => {
  if (!b.length) throw new Error('empty access payload');
  if ((b[0] & 0x80) === 0) return { opcode: b[0], params: b.slice(1) };
  if ((b[0] & 0xc0) === 0x80) return { opcode: readU16be(b, 0), params: b.slice(2) };
  return { opcode: (b[0] << 16) | (b[1] << 8) | b[2], params: b.slice(3) };
};

const netNonce = (type, ctlTtl, seq, src, iv) => concat(
  Uint8Array.of(type, type === 0x03 ? 0 : ctlTtl), u24be(seq), u16be(src), Uint8Array.of(0, 0), u32be(iv),
);

const upperNonce = (devKey, aszmic, seq, src, dst, iv) => concat(
  Uint8Array.of(devKey ? 0x02 : 0x01, aszmic ? 0x80 : 0), u24be(seq), u16be(src), u16be(dst), u32be(iv),
);

/**
 * keys: { netKey, appKey, ivIndex, src, nextSeq(), devKeyFor(addr) }
 *   src        — this phone's own unicast address
 *   devKeyFor  — the device key of the node owning `addr`, or undefined
 */
export class MeshNetwork {
  constructor(keys) {
    this.keys = keys;
    const d = k2(keys.netKey);
    this.nid = d.nid;
    this.encKey = d.encryptionKey;
    this.privKey = d.privacyKey;
    this.aid = k4(keys.appKey);
    this.segRx = new Map();
  }

  // ----- network layer -----
  /** Encrypted + obfuscated network PDU. nonceType 0x00 = network, 0x03 = proxy. */
  encodeNetworkPdu({ ctl, ttl, seq, src, dst, transport, nonceType = 0x00 }) {
    const iv = this.keys.ivIndex;
    const ctlTtl = (ctl ? 0x80 : 0) | (ttl & 0x7f);
    const micLen = ctl ? 8 : 4;
    const nonce = netNonce(nonceType, ctlTtl, seq, src, iv);
    const enc = ccmEncrypt(this.encKey, nonce, concat(u16be(dst), transport), micLen);
    const pecb = aesEcb(this.privKey, concat(new Uint8Array(5), u32be(iv), enc.slice(0, 7)));
    const header = concat(Uint8Array.of(ctlTtl), u24be(seq), u16be(src));
    const obf = header.map((v, i) => v ^ pecb[i]);
    return concat(Uint8Array.of(((iv & 1) << 7) | this.nid), obf, enc);
  }

  /** null when the PDU is not for our network or fails its MIC. */
  decodeNetworkPdu(pdu, nonceType = 0x00) {
    if (pdu.length < 14) return null;
    if ((pdu[0] & 0x7f) !== this.nid) return null;
    const iv = this.keys.ivIndex;
    const enc = pdu.slice(7);
    const pecb = aesEcb(this.privKey, concat(new Uint8Array(5), u32be(iv), enc.slice(0, 7)));
    const h = pdu.slice(1, 7).map((v, i) => v ^ pecb[i]);
    const ctl = (h[0] & 0x80) !== 0;
    const ttl = h[0] & 0x7f;
    const seq = (h[1] << 16) | (h[2] << 8) | h[3];
    const src = (h[4] << 8) | h[5];
    const micLen = ctl ? 8 : 4;
    try {
      const plain = ccmDecrypt(this.encKey, netNonce(nonceType, h[0], seq, src, iv), enc, micLen);
      return {
        ctl, ttl, seq, src, dst: readU16be(plain, 0), transport: plain.slice(2),
      };
    } catch (e) {
      return null;
    }
  }

  // ----- outgoing access messages -----
  /** -> one or more network PDUs ready for the proxy. */
  encodeAccess(dst, accessPayload, useDevKey, ttl = DEFAULT_TTL) {
    const key = useDevKey ? this.keys.devKeyFor(dst) : this.keys.appKey;
    if (!key) throw new Error(`No device key for 0x${dst.toString(16)}`);
    const akf = !useDevKey;
    const aid = akf ? this.aid : 0;
    const { src } = this.keys;
    const firstSeq = this.keys.nextSeq();
    const segmented = accessPayload.length > 11;
    const upper = ccmEncrypt(key, upperNonce(useDevKey, false, firstSeq, src, dst, this.keys.ivIndex), accessPayload, 4);

    if (!segmented) {
      const transport = concat(Uint8Array.of((akf ? 0x40 : 0) | aid), upper);
      return [this.encodeNetworkPdu({
        ctl: false, ttl, seq: firstSeq, src, dst, transport,
      })];
    }
    const seqZero = firstSeq & 0x1fff;
    const segs = [];
    for (let i = 0; i < upper.length; i += 12) segs.push(upper.slice(i, i + 12));
    const segN = segs.length - 1;
    return segs.map((seg, segO) => {
      const hdr = Uint8Array.of(
        0x80 | (akf ? 0x40 : 0) | aid,
        (seqZero >> 6) & 0x7f, // SZMIC = 0
        ((seqZero & 0x3f) << 2) | (segO >> 3),
        ((segO & 0x07) << 5) | segN,
      );
      const seq = segO === 0 ? firstSeq : this.keys.nextSeq();
      return this.encodeNetworkPdu({
        ctl: false, ttl, seq, src, dst, transport: concat(hdr, seg),
      });
    });
  }

  /** Proxy configuration message (e.g. Set Filter Type). */
  encodeProxyConfig(opcode, params) {
    return this.encodeNetworkPdu({
      ctl: true,
      ttl: 0,
      seq: this.keys.nextSeq(),
      src: this.keys.src,
      dst: 0x0000,
      transport: concat(Uint8Array.of(opcode), params),
      nonceType: 0x03,
    });
  }

  // ----- incoming -----
  /**
   * Feed a decoded network message. -> { access?, reply: PDUs to send back
   * (segment acks), note? }
   */
  handleIncoming(net) {
    const t = net.transport;
    const seg = (t[0] & 0x80) !== 0;
    if (net.ctl) {
      const op = t[0] & 0x7f;
      return { reply: [], note: op === 0x00 ? 'segment ack received' : `control opcode 0x${op.toString(16)}` };
    }
    const akf = (t[0] & 0x40) !== 0;
    const aid = t[0] & 0x3f;
    if (!seg) {
      return { access: this.decryptUpper(net.src, net.dst, net.seq, akf, aid, false, t.slice(1)), reply: [] };
    }
    const szmic = (t[1] & 0x80) !== 0;
    const seqZero = ((t[1] & 0x7f) << 6) | (t[2] >> 2);
    const segO = ((t[2] & 0x03) << 3) | (t[3] >> 5);
    const segN = t[3] & 0x1f;
    let seqAuth = (net.seq & ~0x1fff) | seqZero;
    if (seqAuth > net.seq) seqAuth -= 0x2000;
    const id = `${net.src}:${seqAuth}`;
    let st = this.segRx.get(id);
    if (!st) {
      st = {
        segN, parts: new Array(segN + 1).fill(undefined), akf, aid, szmic, seqAuth, done: false,
      };
      this.segRx.set(id, st);
    }
    st.parts[segO] = t.slice(4);
    let block = 0;
    st.parts.forEach((p, i) => { if (p) block |= 1 << i; });
    const ack = this.encodeSegmentAck(net.src, seqZero, block >>> 0);
    if (st.done || st.parts.some((p) => !p)) return { reply: [ack] };
    st.done = true;
    const upper = concat(...st.parts);
    return { access: this.decryptUpper(net.src, net.dst, st.seqAuth, akf, aid, szmic, upper), reply: [ack] };
  }

  encodeSegmentAck(dst, seqZero, block) {
    const transport = concat(
      Uint8Array.of(0x00, (seqZero >> 6) & 0x7f, (seqZero & 0x3f) << 2),
      u32be(block),
    );
    return this.encodeNetworkPdu({
      ctl: true, ttl: DEFAULT_TTL, seq: this.keys.nextSeq(), src: this.keys.src, dst, transport,
    });
  }

  decryptUpper(src, dst, seqAuth, akf, aid, szmic, upper) {
    const micLen = szmic ? 8 : 4;
    let key;
    if (akf) {
      if (aid !== this.aid) return undefined;
      key = this.keys.appKey;
    } else {
      key = this.keys.devKeyFor(src);
    }
    if (!key) return undefined;
    try {
      const plain = ccmDecrypt(key, upperNonce(!akf, szmic, seqAuth, src, dst, this.keys.ivIndex), upper, micLen);
      const { opcode, params } = parseOpcode(plain);
      return {
        src, dst, opcode, params, akf,
      };
    } catch (e) {
      return undefined;
    }
  }
}
